import { accountZone } from "../ui/instant.js";

import { SCHEDULE_MODES } from "../api/mappers.js";

/**
 * FeedScheduleTable
 *
 * The upcoming poll times of a feed pinned to a fixed interval. It exists
 * because "every 15 minutes" is not actionable on its own: an integration that
 * expects a request needs to know when, and a label in the project form does not
 * answer that.
 *
 * Props:
 *   mode — 'fixed' | 'adaptive'
 *   intervalMinutes — number | null
 *   nextPollAt — RFC 3339 string | null, the instant the loop committed to
 *   overdue — whether that instant has already passed *and* nothing is polling
 *   polling — a worker is fetching this feed right now

 *   polls — RFC 3339 strings, the upcoming instants (empty for adaptive)
 *   unreachable — the feed has been given up on and is not being polled
 *   pendingConfirmation — a destructive change is waiting for its re-fetch
 *   generatedAt — when the server computed the list
 *
 * A fixed schedule is a grid: every poll is scheduled from the previous
 * *scheduled* instant, so a worker that runs a second late delays that one
 * request and does not move the series. The list below is therefore the same
 * series the poll loop follows, and the copy says so — "every 10 minutes" that
 * silently became every 10 minutes and 8 seconds was the bug this replaced.
 */

/** Formats an instant in the reader's own timezone, which is the only one they can act on. */
function formatTime(value) {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleTimeString(undefined, { timeZone: accountZone(), hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatDay(value) {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString(undefined, { timeZone: accountZone(), weekday: "short", day: "numeric", month: "short" });
}

/** A short, stable relative label. Server-side humanising would freeze the clock. */
function relative(value, now) {
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return "";
  const seconds = Math.round((at - now.getTime()) / 1000);
  if (seconds < -60) return `${Math.round(-seconds / 60)} min ago`;
  if (seconds < 0) return "any moment";
  if (seconds < 60) return `in ${seconds}s`;
  if (seconds < 3600) return `in ${Math.round(seconds / 60)} min`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes ? `in ${hours}h ${minutes}m` : `in ${hours}h`;
}

/** The two halves of the window, either side of now. */
const RECENT_HOURS = 6;
const FUTURE_HOURS = 24;
const HOUR_MS = 3_600_000;

/**
 * hourBuckets counts polls into the hours of the window.
 *
 * The window is anchored on the current hour rather than on the first instant,
 * so the "now" boundary sits at the same place on the chart every time it is
 * opened. An axis that slid with the data would make two visits look like two
 * different schedules.
 */
function hourBuckets({ polls, recent, now }) {
  const currentHour = Math.floor(now.getTime() / HOUR_MS) * HOUR_MS;
  const offsets = [];
  for (let offset = -(RECENT_HOURS - 1); offset <= FUTURE_HOURS; offset++) offsets.push(offset);

  const counts = new Map(offsets.map((offset) => [offset, 0]));
  for (const at of [...recent, ...polls]) {
    const instant = new Date(at).getTime();
    if (Number.isNaN(instant)) continue;
    const offset = Math.round((Math.floor(instant / HOUR_MS) * HOUR_MS - currentHour) / HOUR_MS);
    if (counts.has(offset)) counts.set(offset, counts.get(offset) + 1);
  }

  return offsets.map((offset) => ({ offset, count: counts.get(offset), past: offset < 0 }));
}

/**
 * ScheduleTimeline is the schedule tab's chart.
 *
 * The two halves are drawn differently on purpose: what happened is solid and
 * what is planned is outlined, because the customer is being shown two different
 * kinds of claim. A single style would present a simulation as a record.
 */
function ScheduleTimeline({ polls, recent, now }) {
  const buckets = hourBuckets({ polls, recent, now });
  const peak = Math.max(1, ...buckets.map((bucket) => bucket.count));
  const planned = buckets.filter((bucket) => !bucket.past).reduce((total, b) => total + b.count, 0);
  const happened = buckets.filter((bucket) => bucket.past).reduce((total, b) => total + b.count, 0);

  return (
    <div>
      <div
        role="img"
        aria-label={`${happened} polls in the last ${RECENT_HOURS} hours, ${planned} planned for the next ${FUTURE_HOURS}`}
        style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 72 }}
      >
        {buckets.map((bucket) => (
          <div
            key={bucket.offset}
            title={`${bucket.offset === 0 ? "this hour" : `${Math.abs(bucket.offset)} h ${bucket.offset < 0 ? "ago" : "ahead"}`}: ${bucket.count} ${bucket.count === 1 ? "poll" : "polls"}`}
            style={{ flex: 1, height: "100%", display: "flex", alignItems: "flex-end" }}
          >
            <div
              style={{
                width: "100%",
                // A count of zero still gets a hairline, so the axis reads as a
                // schedule with quiet hours rather than as missing data.
                height: `${Math.max(bucket.count === 0 ? 2 : 6, Math.round((bucket.count / peak) * 100))}%`,
                background: bucket.past ? "var(--fill-primary)" : "var(--bg-accent)",
                border: bucket.past ? "none" : "1px dashed var(--border-accent)",
                borderRadius: 2,
                boxSizing: "border-box",
              }}
            />
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "var(--text-xs)",
          color: "var(--text-muted)",
          marginTop: 4,
        }}
      >
        <span>−{RECENT_HOURS} h</span>
        <span>{happened} happened</span>
        <span>now</span>
        <span>{planned} planned</span>
        <span>+{FUTURE_HOURS} h</span>
      </div>
    </div>
  );
}

export default function FeedScheduleTable({
  mode = SCHEDULE_MODES.adaptive,
  intervalMinutes = null,
  nextPollAt = null,
  overdue = false,
  polling = false,
  polls = [],
  recent = [],
  unreachable = false,
  pendingConfirmation = false,
  generatedAt = null,
  now = new Date(),
}) {
  if (mode !== SCHEDULE_MODES.fixed) {
    return (
      <div className="ical-card">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
            Polls per hour
          </p>
          <span className="ical-badge ical-badge-neutral">
            {mode === SCHEDULE_MODES.adaptiveGroup ? "adaptive per group" : "adaptive"}
          </span>
        </div>

        <p style={{ margin: "6px 0 14px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {RECENT_HOURS} hours back are the polls that actually happened. The next {FUTURE_HOURS} are
          a <strong style={{ color: "var(--text-primary)" }}>snapshot</strong> — where the scheduler
          would place them if it ran now, so a change detected in the meantime moves everything
          after it.
        </p>

        <ScheduleTimeline polls={polls} recent={recent} now={now} />

        <p style={{ margin: "12px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          {mode === SCHEDULE_MODES.adaptiveGroup
            ? "This project divides its polls between its calendars, so the busiest are polled more often than the quietest."
            : "This feed's own change history decides where its polls land: more in the hours it usually moves, fewer in the ones it does not."}{" "}
          The hour-by-hour distribution is on the Overview tab.
        </p>
      </div>
    );
  }

  return (
    <div className="ical-card">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
        <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-base)" }}>
          Next {polls.length} {polls.length === 1 ? "poll" : "polls"} · every {intervalMinutes} min
        </p>
        {unreachable ? (
          <span className="ical-badge ical-badge-neutral">unreachable</span>
        ) : (
          <span className="ical-badge ical-badge-success">active</span>
        )}
      </div>

      <p style={{ margin: "0 0 12px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
        Times are in your local timezone. The polls sit on a fixed grid: each one is scheduled from
        the previous <em>scheduled</em> instant, so a worker that runs a moment late delays that
        single request and does not move the series. Slots missed during an outage are skipped, not
        queued up.
      </p>

      {unreachable ? (
        <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-danger)" }}>
          This feed has been given up on after repeated failures, so it is not being polled at all.
          The times below resume once the calendar answers again.
        </p>
      ) : null}

      {pendingConfirmation ? (
        <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-warning, inherit)" }}>
          A large deletion is waiting for its confirming re-fetch, about 30 seconds after the poll
          that saw it.
        </p>
      ) : null}

      {polling ? (
        <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          This feed is being polled right now. Its scheduled instant has arrived, so the feed looks
          due until the request finishes — the grid is not affected either way.
        </p>
      ) : null}

      {overdue && !polling ? (
        <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          The next poll is overdue — it was due {relative(nextPollAt, now)}. The worker is behind;
          the cadence resumes on its own.
        </p>
      ) : null}

      {polls.length === 0 ? (
        <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          No polls are scheduled yet. The first one is created when the worker picks the feed up.
        </p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
          <thead>
            <tr style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}>
              <th style={{ textAlign: "left", padding: "4px 8px 4px 0", fontWeight: 500 }}>#</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>When</th>
              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Local time</th>
              <th style={{ textAlign: "left", padding: "4px 0 4px 8px", fontWeight: 500 }}>In</th>
            </tr>
          </thead>
          <tbody>
            {polls.map((poll, index) => (
              <tr key={poll} style={{ borderTop: "1px solid var(--border-subtle, rgba(0,0,0,0.08))" }}>
                <td style={{ padding: "6px 8px 6px 0", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                  {index + 1}
                </td>
                <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{formatDay(poll)}</td>
                <td style={{ padding: "6px 8px", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                  {formatTime(poll)}
                </td>
                <td style={{ padding: "6px 0 6px 8px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                  {relative(poll, now)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p style={{ margin: "12px 0 0", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
        UTC: {polls.length ? polls[0] : "—"}
        {generatedAt ? ` · computed ${generatedAt}` : ""}
      </p>
    </div>
  );
}
